import React, { Suspense, useState, useTransition } from "react";
import { View } from "react-native";
import { AHText, Box } from "arkh";
import { graphql, useFragment, useLazyLoadQuery } from "react-relay";
import { SegmentedControlNew } from "@/src/components/SegmentedControlNew/SegmentedControlNew";
import {
  AnalyticsTimeResolution,
  WorkspaceAnalyticsDimensionType,
  WorkspaceAnalyticsMetric,
} from "../../WorkspaceAnalytics/__generated__/WorkspaceAnalyticsDetailQuery.graphql";
import { useIntlTokens } from "@/src/components/mobile/useIntlTokens";
import { TimeSeriesChartAndTable } from "../../WorkspaceAnalytics/TimeSeriesChartAndTable";
import { LazyQueryNetworkBoundrary } from "@/src/components/mobile/QueryNetworkBoundrary";
import { useWorkspaceId } from "../../workspaceId";
import { WorkspaceRoutineAnalyticsDetailQuery } from "./__generated__/WorkspaceRoutineAnalyticsDetailQuery.graphql";
import { WorkspaceRoutineAnalyticsChartAndTable_query$key } from "./__generated__/WorkspaceRoutineAnalyticsChartAndTable_query.graphql";

export function WorkspaceRoutineOccasionAnalyticsTabScreen(props: {
  routineId: string;
}) {
  return (
    <LazyQueryNetworkBoundrary newFetchKeyOnNavigation>
      {(analyticsFetchKey) => (
        <WorkspaceRoutineAnalyticsComponent
          routineId={props.routineId}
          fetchKey={analyticsFetchKey}
        />
      )}
    </LazyQueryNetworkBoundrary>
  );
}

function WorkspaceRoutineAnalyticsComponent(props: {
  routineId: string;
  fetchKey: string;
}) {
  const workspaceId = useWorkspaceId();

  const [groupByDimension, setGroupByDimension] =
    useState<WorkspaceAnalyticsDimensionType>("STORE");
  const [timeResolution, setTimeResolution] =
    useState<AnalyticsTimeResolution>("WEEK");

  const [metric, setMetric] =
    useState<WorkspaceAnalyticsMetric>("TODO_ISSUE_CREATED");

  const [optimisticMetric, setOptimisticMetric] = useState(metric);
  const [optimisticGroupByDimension, setOptimisticGroupByDimension] =
    useState(groupByDimension);
  const [optimisticTimeResolution, setOptimisticTimeResolution] =
    useState(timeResolution);

  const [chartType, setChartType] = useState<"lines" | "bars">("lines");

  const [isPending, startTransition] = useTransition();
  const data = useLazyLoadQuery<WorkspaceRoutineAnalyticsDetailQuery>(
    graphql`
      query WorkspaceRoutineAnalyticsDetailQuery(
        $routineId: ID!
        $workspaceId: ID!
        $metric: WorkspaceAnalyticsMetric!
        $groupByDimension: WorkspaceAnalyticsDimensionType!
        $timeResolution: AnalyticsTimeResolution!
      ) {
        routine(id: $routineId) {
          id
          title
        }
        ...WorkspaceRoutineAnalyticsChartAndTable_query
      }
    `,
    {
      routineId: props.routineId,
      workspaceId: workspaceId,
      metric: metric,
      groupByDimension: groupByDimension,
      timeResolution: timeResolution,
    },
    {
      fetchKey: props.fetchKey,
    }
  );

  const intl_tokens = useIntlTokens();

  return (
    <View style={{ flex: 1, width: "100%" }}>
      <Box
        style={{ alignItems: "center", flex: 1 }}
        spaceTop="large"
        spaceBottom="medium"
        spaceHorizontal="large"
      >
        <Box
          cornerRadius="large"
          style={{ overflow: "hidden", borderWidth: 1, flex: 1, width: "100%" }}
          borderColor="border-primary"
          color="surface"
        >
          <WorkspaceRoutineAnalyticsHeader
            groupByDimension={optimisticGroupByDimension}
            timeResolution={optimisticTimeResolution}
            chartType={chartType}
            setChartType={setChartType}
            setGroupByDimension={(g) => {
              setOptimisticGroupByDimension(g);
              startTransition(() => {
                setGroupByDimension(g);
              });
            }}
            setTimeResolution={(t) => {
              setOptimisticTimeResolution(t);
              startTransition(() => {
                setTimeResolution(t);
              });
            }}
            metric={optimisticMetric}
            setMetric={(m) => {
              setOptimisticMetric(m);
              startTransition(() => {
                setMetric(m);
              });
            }}
            label={data.routine?.title ?? ""}
          />
          <Suspense fallback={null}>
            <ChartAndTable
              data={data}
              type={chartType}
              loading={isPending}
              dimensionLabel={
                groupByDimension === "STORE" ? intl_tokens.Units : "Workspaces"
              }
            />
          </Suspense>
        </Box>
      </Box>
    </View>
  );
}

function ChartAndTable(props: {
  data: WorkspaceRoutineAnalyticsChartAndTable_query$key;
  type: "lines" | "bars";

  dimensionLabel: string;
  loading: boolean;
}) {
  const data = useFragment(
    graphql`
      fragment WorkspaceRoutineAnalyticsChartAndTable_query on Query {
        workspace(id: $workspaceId) {
          analytics(metric: $metric, filter: { routineId: $routineId }) {
            valueUnit
            connection(
              groupByDimension: $groupByDimension
              timeResolution: $timeResolution
            ) {
              ...TimeSeriesChartAndTable_data
            }
          }
        }
      }
    `,
    props.data
  );
  if (!data.workspace?.analytics) {
    return null;
  }
  return (
    <TimeSeriesChartAndTable
      analytics={data.workspace.analytics.connection}
      type={props.type}
      unit={data.workspace.analytics.valueUnit}
      dimensionLabel={props.dimensionLabel}
      loading={props.loading}
    />
  );
}

function WorkspaceRoutineAnalyticsHeader(props: {
  groupByDimension: WorkspaceAnalyticsDimensionType;
  timeResolution: AnalyticsTimeResolution;
  metric: WorkspaceAnalyticsMetric;
  chartType: "lines" | "bars";
  setChartType: (chartType: "lines" | "bars") => void;
  setGroupByDimension: (
    groupByDimension: WorkspaceAnalyticsDimensionType
  ) => void;
  setTimeResolution: (timeResolution: AnalyticsTimeResolution) => void;
  setMetric: (metric: WorkspaceAnalyticsMetric) => void;
  label: string;
}) {
  const intl_tokens = useIntlTokens();
  return (
    <Box
      style={{
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between",
      }}
      spaceHorizontal="large"
      spaceVertical="medium"
    >
      <Box>
        <AHText style={{ flex: 1 }} type="title-small">
          {props.label}
        </AHText>
      </Box>
      <Box style={{ flexDirection: "row" }} gap="medium">
        <SegmentedControlNew
          items={[
            {
              children: <AHText type="subtitle">Reported Issues</AHText>,
              width: 130,
              value: "TODO_ISSUE_CREATED" satisfies WorkspaceAnalyticsMetric,
            },
            {
              width: 130,
              children: <AHText type="subtitle">Resolved Issues</AHText>,
              value: "TODO_ISSUE_RESOLVED" satisfies WorkspaceAnalyticsMetric,
            },
            {
              width: 140,
              children: <AHText type="subtitle">Tasks Completed</AHText>,
              value:
                "TASK_EXECUTIONS_COMPLETED" satisfies WorkspaceAnalyticsMetric,
            },
            {
              width: 180,
              children: (
                <AHText type="subtitle">Routine Occasion Completed</AHText>
              ),
              value:
                "ROUTINE_OCCASION_COMPLETED" satisfies WorkspaceAnalyticsMetric,
            },
          ]}
          selected={props.metric}
          setSelected={props.setMetric}
        />
        <SegmentedControlNew
          items={[
            {
              children: <AHText type="subtitle">Line</AHText>,
              width: 40,
              value: "lines" satisfies "lines" | "bars",
            },
            {
              width: 40,
              children: <AHText type="subtitle">Bar</AHText>,
              value: "bars" satisfies "lines" | "bars",
            },
          ]}
          selected={props.chartType}
          setSelected={props.setChartType}
        />

        <SegmentedControlNew
          items={[
            {
              children: <AHText type="subtitle">M</AHText>,
              width: 40,
              value: "MONTH" satisfies AnalyticsTimeResolution,
            },
            {
              width: 40,
              children: <AHText type="subtitle">W</AHText>,
              value: "WEEK" satisfies AnalyticsTimeResolution,
            },
          ]}
          selected={props.timeResolution}
          setSelected={props.setTimeResolution}
        />
        <SegmentedControlNew
          items={[
            {
              children: <AHText type="subtitle">{intl_tokens.Unit}</AHText>,
              width: 100,
              value: "STORE" satisfies WorkspaceAnalyticsDimensionType,
            },
            {
              width: 100,
              children: <AHText type="subtitle">Workspace</AHText>,
              value:
                "WORKSPACE_OWNER" satisfies WorkspaceAnalyticsDimensionType,
            },
          ]}
          selected={props.groupByDimension}
          setSelected={props.setGroupByDimension}
        />
      </Box>
    </Box>
  );
}
